<template>
  <div
    id="holiday-work-requests"
    class="block block-rounded mb-4"
    aria-label="Working during Public Holiday"
  >
    <div class="block-header block-header-default">
      <h3 class="block-title">
        Working during Public Holiday
      </h3>
    </div>
    <div class="block-content">
      <div v-if="isLoading" class="text-center pt-3 pb-3">
        <span class="spinner-border text-primary" role="status" aria-hidden="true"></span>
        <span class="ms-2">Loading holiday work requests...</span>
      </div>
      <div
        v-else-if="holidayWorkList.length"
      >
        <!-- Year Statistics Table -->
        <div class="table-responsive mb-4">
          <table class="table table-sm table-striped">
            <thead>
              <tr>
                <th>Year</th>
                <th>Total Days</th>
                <th>Approved Days</th>
                <th>Pending Days</th>
                <th>Rejected Days</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="year in sortedYears"
                :key="year"
              >
                <td class="fw-semibold">
                  {{ year }}
                </td>
                <td>{{ holidayWorkListSortByYear[year].totalCount }}</td>
                <td>{{ holidayWorkListSortByYear[year].approvedCount }}</td>
                <td>{{ holidayWorkListSortByYear[year].pendingCount }}</td>
                <td>{{ holidayWorkListSortByYear[year].rejectedCount }}</td>
                <td>
                  <a
                    :href="`/admin/vacation/holiday-work-list/${year}?staff_id=${staffId}`"
                    class="btn btn-sm btn-outline-primary"
                  >
                    View
                  </a>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      <div
        v-else
        class="text-center py-4 text-muted"
      >
        <p class="mb-2">
          No holiday work submissions
        </p>
        <button
          v-if="!isSuperAdmin"
          type="button"
          class="btn btn-sm btn-outline-primary"
          @click="$emit('open-form')"
        >
          Submit Holiday Work
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

export default {
    name: "HolidayWorkRequestsList",
    props: {
        staffId: {
            type: Number,
            required: true,
        },
        isSuperAdmin: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            submitting: false,
            reminding: false,
            isLoading: true,
            currentLoadingId: null,
            currentLoadingType: null,
        };
    },
    computed: {
        ...mapGetters({
            holidayWorkList: "vacation/holidayWorkList",
        }),
        holidayWorkListSortByYear() {
            const yearlyList = this.groupHolidayWorkByYear(this.holidayWorkList);
            return this.calculateYearlyStats(yearlyList);
        },
        sortedYears() {
            return Object.keys(this.holidayWorkListSortByYear).sort((a, b) => Number(b) - Number(a));
        },
        currentYear() {
            return new Date().getFullYear();
        },
    },
    mounted() {
        this.fetchList();
    },
    methods: {
        async fetchList() {
            this.isLoading = true;
            try {
                await this.$store.dispatch("vacation/getHolidayWorkRequests", { staffId: this.staffId });
            } catch {
            } finally {
                this.isLoading = false;
            }
        },
        getStatus(item) {
            if (item.status === "approved") return "Approved";
            if (item.status === "rejected") return "Rejected";
            if (item.status === "pending") return "Pending";
            return item.status || "Submitted";
        },
        statusBadgeClass(item) {
            if (item.status === "approved") return "bg-success";
            if (item.status === "rejected") return "bg-danger";
            return "bg-info";
        },
        async approveRequest(item) {
            try {
                this.currentLoadingId = item.id;
                this.currentLoadingType = "approve";
                this.submitting = true;
                await this.managerUpdateRequest({ id: item.id, status: "approved", type: "holiday_work" });
                this.$emit("update");
            } catch {
            } finally {
                this.submitting = false;
                this.currentLoadingId = null;
                this.currentLoadingType = null;
            }
        },
        async rejectRequest(item) {
            try {
                this.currentLoadingId = item.id;
                this.currentLoadingType = "reject";
                this.submitting = true;
                await this.managerUpdateRequest({ id: item.id, status: "rejected", type: "holiday_work" });
                this.$emit("update");
            } catch {
            } finally {
                this.submitting = false;
                this.currentLoadingId = null;
                this.currentLoadingType = null;
            }
        },
        async cancelRequest(item) {
            try {
                if (confirm("Do you want to cancel this holiday work request?") === false) {
                    return;
                }
                this.currentLoadingId = item.id;
                this.currentLoadingType = "cancel";
                this.submitting = true;
                await this.cancelHolidayWorkRequest({ id: item.id });
                alert("Holiday work request has been cancelled");
                this.$emit("update");
            } catch (error) {
                alert("An error occurred while cancelling the request");
                console.log(error);
            } finally {
                this.submitting = false;
                this.currentLoadingId = null;
                this.currentLoadingType = null;
            }
        },
        async remindRequest(item) {
            try {
                if (confirm("Do you want to send a reminder for this holiday work request?") === false) {
                    return;
                }
                this.currentLoadingId = item.id;
                this.currentLoadingType = "remind";
                this.reminding = true;
                await this.remindHolidayWorkRequest({ id: item.id });
                alert("Reminder has been sent");
                this.$emit("update");
            } catch (error) {
                alert("An error occurred while sending the reminder");
                console.log(error);
            } finally {
                this.reminding = false;
                this.currentLoadingId = null;
                this.currentLoadingType = null;
            }
        },
        ...mapActions({
            managerUpdateRequest: "vacation/managerUpdateRequest",
            cancelHolidayWorkRequest: "vacation/cancelHolidayWorkRequest",
            remindHolidayWorkRequest: "vacation/remindHolidayWorkRequest",
        }),
        groupHolidayWorkByYear(holidayWorkList) {
            const yearlyHolidayWork = {};
            for (const holidayWork of holidayWorkList) {
                const startDate = new Date(holidayWork.start_date);
                const endDate = new Date(holidayWork.end_date);
                const startYear = startDate.getFullYear();
                const endYear = endDate.getFullYear();

                if (!yearlyHolidayWork[startYear]) {
                    yearlyHolidayWork[startYear] = [];
                }
                yearlyHolidayWork[startYear].push({ ...holidayWork, _yearContext: startYear });

                // Handle requests that span multiple years
                if (startYear !== endYear) {
                    if (!yearlyHolidayWork[endYear]) {
                        yearlyHolidayWork[endYear] = [];
                    }
                    yearlyHolidayWork[endYear].push({ ...holidayWork, _yearContext: endYear });
                }
            }
            return yearlyHolidayWork;
        },
        calculateRequestDaysInYear(holidayWork, year) {
            const yearStart = new Date(year, 0, 1);
            const yearEnd = new Date(year, 11, 31);
            const start = new Date(Math.max(new Date(holidayWork.start_date), yearStart));
            const end = new Date(Math.min(new Date(holidayWork.end_date), yearEnd));
            if (end < start) return 0;
            
            let days = Math.round((end - start) / (1000 * 60 * 60 * 24)) + 1;
            
            // Apply half-day adjustment if set
            if (holidayWork.half_day === 1) {
                days = 0.5;
            }
            
            return days;
        },
        calculateYearlyStats(holidayWorkByYear) {
            const yearlyStats = {};
            for (const year in holidayWorkByYear) {
                const holidayWorkList = holidayWorkByYear[year];
                let totalCount = 0;
                let approvedCount = 0;
                let pendingCount = 0;
                let rejectedCount = 0;

                for (const holidayWork of holidayWorkList) {
                    const days = this.calculateRequestDaysInYear(holidayWork, Number(year));
                    totalCount += days;
                    if (holidayWork.status === "approved") {
                        approvedCount += days;
                    } else if (holidayWork.status === "pending") {
                        pendingCount += days;
                    } else if (holidayWork.status === "rejected") {
                        rejectedCount += days;
                    }
                }

                yearlyStats[year] = {
                    totalCount,
                    approvedCount,
                    pendingCount,
                    rejectedCount,
                    holidayWorkRequests: holidayWorkList,
                };
            }
            return yearlyStats;
        },
    },
};
</script>
